import { useProjectParams } from '@hooks/useProjectParams';
import { ChooseSectionText } from 'components/ChooseSectionText';
import { DropdownContext } from 'components/DropdownContext';
import { SelectVersionsWorkOrder } from 'components/SelectVersionsWorkOrder';
import { Toolbar } from 'components/Toolbar';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { WorkOrderConstructionTypesTabSelect } from 'components/WorkOrderConstructionTypesTabSelect';
import { filtersWorkOrderSlice } from 'features/filtersWorkOrder/filtersWorkOrderSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ActKc2NavData, WorkOrderActKc2Data } from 'gql/graphql';
import {
    ActKc2StatusEnum,
    ContractStatusEnum,
    CostGroupEnum,
    ProjectMethodologyEnum,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
    WorkOrderVorTypeEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { useScrollToWithNavigationInfo } from 'hooks/useScrollToWithNavigationInfo';
import { EditOtherModal } from 'pages/Project/Common/Contract/OtherEdit';
import { OtherViewModal } from 'pages/Project/Common/Contract/OtherView';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { EditMtrDialog } from 'pages/Project/UerProject/PriceList/EditMtrDialog';
import { CreateOrEditRatio } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/CreateOrEditRatio';
import { CreateOrEditUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer/CreateOrEditUer';
import { cnMixSpace, Grid, GridItem, IconArrowDown, IconWarning, Informer } from 'uikit';
import { isNumber } from 'utils';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type { AgGridReact } from 'ag-grid-react';
import { noun } from 'plural-ru';
import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { MatchMtrDialog } from '../MatchMtrDialog';
import { WorkOrderUseCoefficient } from '../WorkOrderUseCoefficientModal/WorkOrderUseCoefficient';

import { AddActKC2 } from './AddActKC2';
import { AddMtrDialog } from './AddMtrDialog';
import { AddOthers } from './AddOrderOther';
import { EditOtherDistribution } from './EditOtherDistribution';
import { useAddToWorkOrderMenu } from './hooks/useAddToWorkOrderMenu';
import { useConstructionTypes } from './hooks/useConstructionTypes';
import { useSeasons } from './hooks/useSeasons';
import { useWorkOrderObjects } from './hooks/useWorkOrderObjects';
import { UploadWorkOrder } from './UploadWorkOrder/UploadWorkOrder';
import { WorkOrderAddUER } from './WorkOrderAddUER';
import { ModalWorkOrderEnum, WorkOrderStatusLabels } from './workOrderConstants';
import { WorkOrderFilterForm } from './WorkOrderFilterForm';
import { WorkOrderKebabMenu } from './WorkOrderKebabMenu';
import { WorkOrderOtherTable } from './WorkOrderOthersTable';
import { useGetWorkOrderOtherDataGetter } from './WorkOrderOthersTable/hooks';
import { TABLE_SETTINGS_HEIGHT, WorkOrderPrice } from './WorkOrderPrice';
import { WorkOrderPriceDeleteOneItem } from './WorkOrderPriceDeleteOneItem';
import { gqlWorkOrderVorTypeAndStatusFollowers, uerWorkOrderDraftInfo, workOrderVersionsGql } from './workOrderQueries';
import { WorkOrderRequisites } from './WorkOrderRequisites';
import { WorkOrderSummaryTable } from './WorkOrderSummaryTable';
import { WorkOrderChapters } from './workorderTypes';

import './WorkOrder.css';

const WORK_ORDER_PATH_PATTERN = '/projects/:methodology/:id/order/:orderId/old';

export const WorkOrder = () => {
    const { id: projectId, orderId, methodology } = useProjectParams();

    const [position, setPosition] = useState<number | undefined>();
    const [activeModal, setActiveModal] = useState<undefined | ModalWorkOrderEnum>();
    const refBtn = useRef<HTMLButtonElement>(null);
    const gridTableRef = useRef<AgGridReact>(null);
    const { order: orderRules } = useAccess();

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const openModal = (modalType: ModalWorkOrderEnum) => setActiveModal(modalType);
    const closeModal = () => setActiveModal(undefined);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        state,
    }: {
        state: { reload: boolean | number; openEditWorkOrder?: boolean; actNavInfo?: any };
    } = useLocation();

    const { projectData, refreshProject } = useProjectData(projectId);
    const getContractData = projectData?.contractNavigation?.data?.contractNavData;
    const statusProject = projectData?.status;

    const isProjectStatusDelayed = projectData?.status === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = projectData?.status === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = getContractData?.status === ContractStatusEnum.InImplementation;
    const workOrderId = Number(orderId);

    const [constructionTypeId, setConstructionTypeId] = useState(0);
    const [filters, setFilters] = useState<any>({});
    const [constructionTypeHasActKC2, setConstructionTypeHasActKC2] = useState(false);

    const [constructionTypes, constructionTypesStatus, refreshConstructionTypes] = useConstructionTypes(workOrderId);
    const [, , , refreshSeasonsData] = useSeasons(workOrderId, false);
    const { workOrderObjects, patchedObjectForWorkOrderFilter, refreshWorkOrderObjects } =
        useWorkOrderObjects(workOrderId);

    const [workOrderVersionsData, , , workOrderVersionsRequest] = useRequest({
        requestId: 'workOrderVersions',
        query: workOrderVersionsGql,
        withEffect: false,
    });

    const refreshVersionsData = useCallback(() => {
        dispatch(workOrderVersionsRequest({ workOrderId }));
    }, [dispatch, workOrderId, workOrderVersionsRequest]);

    const variables = useMemo(() => ({ workOrderId }), [workOrderId]);

    const [workOrderVorTypeData] = useRequest({
        requestId: 'workOrderVorType',
        query: gqlWorkOrderVorTypeAndStatusFollowers,
        variables,
    });

    const vorType = workOrderVorTypeData?.uerWorkOrder?.data?.vorType;
    const isWorkOrderVorTypeXml = vorType === WorkOrderVorTypeEnum.XmlBundle;

    useEffect(() => {
        dispatch(workOrderVersionsRequest({ workOrderId }));
    }, [dispatch, workOrderId, workOrderVersionsRequest]);

    useEffect(() => {
        dispatch(filtersWorkOrderSlice.actions.setFiltersWorkOrder(filters));
    }, [filters, dispatch]);

    useEffect(() => {
        if (state?.openEditWorkOrder) {
            setTimeout(() => refBtn.current?.click(), 500);
        }
    }, [state?.openEditWorkOrder]);

    const isOneOfThePriceChaptersSelected = useMemo(
        () =>
            [WorkOrderChapters.CHAPTER1_7, WorkOrderChapters.CHAPTER8, WorkOrderChapters.CHAPTER9].includes(
                filters.chapter,
            ),
        [filters.chapter],
    );
    const isOthersChapterSelected = filters.chapter === WorkOrderChapters.OTHERS;
    const isSummaryChapterSelected = filters.chapter === WorkOrderChapters.SUMMARY;

    const isShowTablePossible = useMemo(
        () =>
            (isSummaryChapterSelected && filters.chapter) ||
            (isOneOfThePriceChaptersSelected && filters.subObjectId && filters.chapter) ||
            (isOthersChapterSelected && filters.objectId && filters.chapter),
        [
            filters.chapter,
            filters.objectId,
            filters.subObjectId,
            isOneOfThePriceChaptersSelected,
            isOthersChapterSelected,
            isSummaryChapterSelected,
        ],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const firstRenderWorkOrderFilterSetter = useCallback(
        ({ savedValuesObj }) => {
            const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
            const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

            if (savedConstructionTypeId && !state?.actNavInfo) {
                setConstructionTypeId(savedConstructionTypeId);
            }

            if (!state?.actNavInfo) {
                setFilters(savedFilters ?? {});
            }
        },
        [state?.actNavInfo],
    );

    const firstRenderWorkOrderEmptyFilterSetter = useMemo(() => {
        if (constructionTypes?.length && !state?.actNavInfo) {
            return (setter: (params: { key: string | undefined; newState: any }) => void) => {
                const newConstructionTypeId = constructionTypes[0].id;

                setConstructionTypeId(newConstructionTypeId);
                setter({
                    key: 'constructionTypeId',
                    newState: newConstructionTypeId,
                });
            };
        }

        return null;
    }, [constructionTypes, state?.actNavInfo]);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: firstRenderWorkOrderFilterSetter,
                withEmptyFilterSetter: firstRenderWorkOrderEmptyFilterSetter,
                matchPathPattern: WORK_ORDER_PATH_PATTERN,
                unableToProcess: constructionTypesStatus === RequestStatus.FETCHING,
            }),
            [constructionTypesStatus, firstRenderWorkOrderEmptyFilterSetter, firstRenderWorkOrderFilterSetter],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionTypeId) => {
            setConstructionTypeId(newConstructionTypeId);
            setFilters(savedFilterState?.[newConstructionTypeId] ?? {});

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionTypeId,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const handleFilterFormChange = useCallback(
        (newFilters, justForSaveConstructionTypeId?: number) => {
            let mergedFilters = {};

            setFilters((oldFilters: any) => {
                if (
                    oldFilters.chapter &&
                    oldFilters.chapter !== newFilters.chapter &&
                    newFilters.chapter === WorkOrderChapters.OTHERS
                ) {
                    mergedFilters = { ...oldFilters, ...newFilters, subObjectId: undefined };

                    return mergedFilters;
                }
                if (newFilters.chapter === WorkOrderChapters.SUMMARY) {
                    mergedFilters = { ...newFilters, objectId: undefined, subObjectId: undefined };

                    return mergedFilters;
                }

                mergedFilters = { ...oldFilters, ...newFilters };

                return mergedFilters;
            });

            handleSaveFilterState({
                key: justForSaveConstructionTypeId ? String(justForSaveConstructionTypeId) : String(constructionTypeId),
                newState: mergedFilters,
            });
        },
        [constructionTypeId, handleSaveFilterState],
    );

    const handleRequisitesChanged = useCallback(
        (callVersionsRenew: boolean) => {
            refreshProject();

            if (callVersionsRenew) {
                dispatch(workOrderVersionsRequest({ workOrderId }));
            }
        },
        [dispatch, refreshProject, workOrderId, workOrderVersionsRequest],
    );

    const checkedWorkOrderNavs = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((workOrder) => workOrder.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const titleWorkOrder = useMemo(() => getLabelWorkOrder(checkedWorkOrderNavs), [checkedWorkOrderNavs]);

    const onModalClose = useCallback(
        (newActId?: number) => {
            const navState = {
                reload: Math.random(),
                actNavInfo: {
                    actId: newActId,
                },
            };

            setTimeout(() => navigate('./', { replace: true, state: navState }));
        },
        [navigate],
    );

    const onEditOtherModalClose = useCallback(
        (isSuccess?: boolean) => {
            if (isSuccess) {
                onModalClose();
            } else {
                setTimeout(() => navigate('./'));
            }
        },
        [navigate, onModalClose],
    );

    const onActKCAddOrEdit = useCallback(
        (newActId?: number) => {
            refreshProject();
            onModalClose(newActId);
        },
        [onModalClose, refreshProject],
    );

    const onUploadWorkOrder = useCallback(() => {
        refreshConstructionTypes();
        refreshWorkOrderObjects();
        refreshSeasonsData();
        onModalClose();
    }, [onModalClose, refreshConstructionTypes, refreshSeasonsData, refreshWorkOrderObjects]);

    const statusWorkOrder = checkedWorkOrderNavs?.status;

    const isNotActKC2 = checkedWorkOrderNavs?.actKC2Navs?.length === 0;
    const isStatusWorkOrderInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    const isStatusWorkOrderOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;

    const checkConstructionTypeHasActKC2Handler = useCallback(
        (arrForCheck: WorkOrderActKc2Data[] | undefined | null) => {
            setConstructionTypeHasActKC2(!(!arrForCheck || !Array.isArray(arrForCheck) || !arrForCheck.length));
        },
        [],
    );

    const isAllActKC2Accepted = useMemo(() => {
        const acceptedActs = checkedWorkOrderNavs?.actKC2Navs?.filter(
            ({ status }: ActKc2NavData) => status === ActKc2StatusEnum.Accepted,
        );

        return checkedWorkOrderNavs?.actKC2Navs?.length === acceptedActs?.length;
    }, [checkedWorkOrderNavs]);

    const { workOrderActsList } = useGetWorkOrderOtherDataGetter({
        workOrderId,
        workOrderRootObjectId: filters.objectId,
        withEffect: false,
    });

    const isNotOtherActKC2 = workOrderActsList?.length === 0;

    const isOtherActInWork = useMemo(
        () =>
            !!workOrderActsList?.filter(
                (itemAct) =>
                    itemAct.status === ActKc2StatusEnum.InProgress || itemAct.status === ActKc2StatusEnum.OnCheck,
            )?.length,
        [workOrderActsList],
    );

    const isNotChosenFiltersOther =
        (isStatusWorkOrderOnFormation || isStatusWorkOrderInProgress) &&
        (!isOthersChapterSelected || !filters.objectId);

    const isNotActKC2OtherInProgress =
        isOthersChapterSelected &&
        filters.objectId &&
        isStatusWorkOrderInProgress &&
        (isNotOtherActKC2 || !isOtherActInWork);

    const isHaveTooltipOther = isNotActKC2OtherInProgress || isNotChosenFiltersOther;

    const workOrderHasInProgress = useMemo<boolean>(
        () => checkedWorkOrderNavs?.metadata?.hasInProgress,
        [checkedWorkOrderNavs?.metadata?.hasInProgress],
    );
    const canRollbackWorkOrderChange = useMemo<boolean>(
        () => checkedWorkOrderNavs?.metadata?.canRollbackWorkOrderChange,
        [checkedWorkOrderNavs?.metadata?.canRollbackWorkOrderChange],
    );

    const tableRef = useRef<HTMLDivElement>(null);

    const isAddOtherBtnDisabled =
        !isOthersChapterSelected ||
        !filters.objectId ||
        isSummaryChapterSelected ||
        !isContractStatusWork ||
        statusWorkOrder === WorkOrderStatusEnum.Delayed ||
        (isStatusWorkOrderInProgress && isNotOtherActKC2) ||
        (isStatusWorkOrderInProgress && !isOtherActInWork) ||
        (isStatusWorkOrderInProgress && !constructionTypeHasActKC2);

    const isWorkOrderRequisitesDisabled = isSummaryChapterSelected || !isContractStatusWork || !orderRules.canEdit;

    const {
        addToWorkOrderMenuItems,
        isAddOtherOpened,
        closeModalsHandler,
        isAddActKC2Opened,
        isUerModalOpened,
        getMenuItemAttributes,
        onMenuMouseMove,
        tooltipPosition,
        actKc2TooltipActive,
        actKc2OtherTooltipActive,
        uerTooltipActive,
        onMenuMouseLeave,
    } = useAddToWorkOrderMenu({
        isAllActKC2Accepted,
        isNotActKC2,
        isAddOtherBtnDisabled,
        isDisabledByProjectStatus,
        statusWorkOrder,
        filters,
        statusContract: getContractData?.status,
        // isSomeActKC2Accepted,
        isHaveTooltipOther,
        openModal,
    });

    const draftVars = useMemo(
        () => ({
            workOrderId,
        }),
        [workOrderId],
    );

    const [draftWorkOrderData, , , draftWorkOrderRequest, resetDraftData] = useRequest({
        requestId: 'draftWorkOrderCounterUer',
        query: uerWorkOrderDraftInfo,
        variables: draftVars,
        withEffect: isStatusWorkOrderOnFormation,
    });

    useEffect(() => {
        if (state?.reload && isStatusWorkOrderOnFormation) {
            dispatch(draftWorkOrderRequest(draftVars));
        }

        return () => {
            if (!state?.reload) {
                dispatch(resetDraftData(undefined));
            }
        };
    }, [dispatch, draftVars, draftWorkOrderRequest, isStatusWorkOrderOnFormation, resetDraftData, state?.reload]);

    const draftInformer = useMemo(() => {
        const draftCount = draftWorkOrderData?.uerWorkOrderDraftInfo?.data?.draftCount;

        if (draftCount) {
            const msg = `${draftCount} ${noun(draftCount, ...['запись', 'записи', 'записей'])}  в статусе черновик`;

            return (
                <Informer
                    label={msg}
                    status="alert"
                    view="filled"
                    size="s"
                    icon={IconWarning}
                    className="workOrderDraftInformer"
                />
            );
        }

        return null;
    }, [draftWorkOrderData?.uerWorkOrderDraftInfo?.data?.draftCount]);

    const isUploadWorkOrderDisabled = useMemo(
        () =>
            !isWorkOrderVorTypeXml ||
            !isStatusWorkOrderOnFormation ||
            isDisabledByProjectStatus ||
            !isContractStatusWork,
        [isContractStatusWork, isDisabledByProjectStatus, isStatusWorkOrderOnFormation, isWorkOrderVorTypeXml],
    );

    const uploadWorkOrderTooltip = useMemo(() => {
        if (!isWorkOrderVorTypeXml || isStatusWorkOrderInProgress) {
            return !isWorkOrderVorTypeXml
                ? 'Доступно только для ВОР формата xml'
                : 'В статусе "В работе" недоступно добавление новых ВОР';
        }

        return '';
    }, [isStatusWorkOrderInProgress, isWorkOrderVorTypeXml]);

    useEffect(() => {
        if (tableRef.current) {
            setPosition(tableRef.current?.getBoundingClientRect?.()?.top);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableRef, isHeaderOpened, tableRef.current?.getBoundingClientRect?.()?.top]);

    useScrollToWithNavigationInfo({
        constructionTypeSetter: handleConstructionTypeChange,
        objectSetter: handleFilterFormChange,
        objects: workOrderObjects,
        tableApi: gridTableRef?.current,
    });

    const reloadWorkOrderTable = useCallback(() => {
        if (gridTableRef?.current) {
            gridTableRef?.current?.api?.refreshInfiniteCache?.();
        }
    }, []);

    const costRounding = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    return (
        <ProjectPageLayout
            title={titleWorkOrder}
            statusWorkOrder={statusWorkOrder}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            headerRightSide={draftInformer}
            isHeaderCollapsable
            layoutEntityKey={workOrderId}
            headerBottom={
                <Toolbar flex fullLine>
                    <WorkOrderRequisites
                        workOrderId={workOrderId}
                        onChange={handleRequisitesChanged}
                        isDisabled={isWorkOrderRequisitesDisabled || isDisabledByProjectStatus}
                        refBtn={refBtn}
                        orderRules={orderRules}
                        projectId={projectId}
                        status={statusWorkOrder}
                    />
                    <UploadWorkOrder
                        workOrderId={workOrderId}
                        onSuccess={onUploadWorkOrder}
                        vorType={vorType}
                        isDisabled={isUploadWorkOrderDisabled}
                        tooltipMessage={uploadWorkOrderTooltip}
                    />
                    {!!workOrderVersionsData?.uerWorkOrderVersions?.length && !isStatusWorkOrderOnFormation && (
                        <SelectVersionsWorkOrder items={workOrderVersionsData?.uerWorkOrderVersions} />
                    )}
                    <WorkOrderKebabMenu
                        workOrderId={workOrderId}
                        contractId={getContractData?.id}
                        statusProject={statusProject}
                        statusContract={getContractData?.status}
                        projectData={projectData}
                        workOrderLabel={titleWorkOrder}
                        refreshProjectData={refreshProject}
                        statusWorkOrder={statusWorkOrder}
                        isDisabledByProjectStatus={isDisabledByProjectStatus}
                        isNotActKC2={isNotActKC2}
                        refreshWorkOrder={onModalClose}
                        isWorkOrderVorTypeXml={isWorkOrderVorTypeXml}
                        canRollbackWorkOrderChange={canRollbackWorkOrderChange}
                        refreshVersionsData={refreshVersionsData}
                    />
                </Toolbar>
            }
        >
            <div
                id="workOrder"
                style={
                    {
                        '--table-settings-height': `${TABLE_SETTINGS_HEIGHT}px`,
                        '--others-height': isNumber(position) && `${position + TABLE_SETTINGS_HEIGHT + 20}px`,
                    } as React.CSSProperties
                }
            >
                <AddActKC2
                    gridTableRef={gridTableRef?.current}
                    contractor={getContractData?.metadata?.contractor}
                    contractorAddress={getContractData?.metadata?.contractorAddress}
                    workOrderId={workOrderId}
                    constructionTypeId={constructionTypeId}
                    objectId={filters.objectId}
                    chapter={filters.chapter}
                    costGroup={filters.chapter === 'OTHERS' ? CostGroupEnum.Others : CostGroupEnum.Cmr}
                    statusWorkOrder={statusWorkOrder}
                    onAdd={onActKCAddOrEdit}
                    statusContract={getContractData?.status}
                    isDisabled={isDisabledByProjectStatus}
                    isOpenedOuter={isAddActKC2Opened}
                    closeOuterHandler={closeModalsHandler}
                    withBtn={false}
                    tooltipPosition={tooltipPosition}
                    tooltipOuterOpened={actKc2TooltipActive}
                />
                <AddOthers
                    workOrderId={workOrderId}
                    constructionTypeId={constructionTypeId}
                    chapter={filters.chapter}
                    onChange={onModalClose}
                    isDisabled={isAddOtherBtnDisabled || isDisabledByProjectStatus}
                    workOrderStatus={statusWorkOrder}
                    workOrderObjectId={filters.objectId}
                    isOpenedOuter={isAddOtherOpened}
                    closeOuterHandler={closeModalsHandler}
                    withBtn={false}
                    tooltipPosition={tooltipPosition}
                    tooltipOuterOpened={actKc2OtherTooltipActive}
                    isNotChosenFiltersOther={isNotChosenFiltersOther}
                />
                <WorkOrderAddUER
                    filters={filters}
                    reloadWorkOrder={onModalClose}
                    constructionTypeId={constructionTypeId}
                    workOrderStatus={statusWorkOrder}
                    contractId={projectData?.contractId}
                    tooltipPosition={tooltipPosition}
                    tooltipOuterOpened={uerTooltipActive}
                    isOpenedOuter={isUerModalOpened}
                    closeOuterHandler={closeModalsHandler}
                />
                <WorkOrderConstructionTypesTabSelect
                    onChange={handleConstructionTypeChange}
                    constructionTypes={constructionTypes}
                    activeConstructionTypeId={constructionTypeId}
                />
                <Grid
                    cols={10}
                    gap="xl"
                    rowGap="xl"
                    colGap="xl"
                    className={cnMixSpace({ mT: 'xl', mB: 'xl' })}
                    style={{ overflow: 'auto' }}
                >
                    <GridItem col={6}>
                        <WorkOrderFilterForm
                            defaultValues={filters}
                            onChange={handleFilterFormChange}
                            constructionTypeId={constructionTypeId}
                            workOrderObjects={patchedObjectForWorkOrderFilter}
                        />
                    </GridItem>
                    <GridItem col={4}>
                        <TooltipWrapper
                            direction="upCenter"
                            message={`Работа с НЗ недоступна в статусе "${
                                statusWorkOrder && WorkOrderStatusLabels[statusWorkOrder]
                            }"`}
                            isHidden={
                                statusWorkOrder === WorkOrderStatusEnum.InProgress ||
                                statusWorkOrder === WorkOrderStatusEnum.OnFormation
                            }
                            style={{ width: '115px' }}
                            toolTipClassName="toolTipDropdownClassName"
                        >
                            <DropdownContext
                                wrapperClassName={cnMixSpace({ mR: 'xs' })}
                                items={addToWorkOrderMenuItems}
                                btnView="primary"
                                btnSize="s"
                                btnIconSize="s"
                                btnLabel="Добавить"
                                icon={IconArrowDown}
                                getItemAttributesHandler={getMenuItemAttributes}
                                onMouseMoveHandler={onMenuMouseMove}
                                onMouseLeaveHandler={onMenuMouseLeave}
                                isDisabled={
                                    !orderRules.canEdit ||
                                    statusWorkOrder === WorkOrderStatusEnum.Completed ||
                                    statusWorkOrder === WorkOrderStatusEnum.Delayed
                                }
                                className="widthMenu"
                            />
                        </TooltipWrapper>
                    </GridItem>
                </Grid>
                <Routes>
                    <Route
                        path="/addMtr/:uerId"
                        element={
                            <AddMtrDialog
                                workOrderStatus={statusWorkOrder}
                                shouldAddToActKC2={statusWorkOrder === WorkOrderStatusEnum.InProgress}
                            />
                        }
                    />
                    <Route
                        path="/editMtr/:mtrId/:workOrderPriceId"
                        element={
                            <EditMtrDialog
                                updateMtr={onModalClose}
                                chapter={filters.chapter}
                                workOrderStatus={statusWorkOrder}
                            />
                        }
                    />
                    <Route
                        path="/matchMtr/:workOrderPriceId/:priceListId"
                        element={
                            <MatchMtrDialog
                                contractId={projectData?.contractId}
                                onMatchApply={onModalClose}
                                refreshProject={refreshProject}
                                chapter={filters.chapter}
                                workOrderStatus={statusWorkOrder}
                                isChange={false}
                            />
                        }
                    />
                    <Route
                        path="/removeRow/:rowId"
                        element={<WorkOrderPriceDeleteOneItem gridTableRef={gridTableRef} />}
                    />
                    <Route
                        path="/editDist/:otherId"
                        element={
                            /* @ts-ignore */
                            <EditOtherDistribution
                                onClose={onModalClose}
                                workOrderId={workOrderId}
                                currentConstructionTypeId={constructionTypeId}
                                costRounding={costRounding}
                                workOrderStatus={statusWorkOrder}
                            />
                        }
                    />
                    <Route
                        path="/editOther/:otherId"
                        element={
                            <EditOtherModal
                                onClose={onEditOtherModalClose}
                                contractId={projectData?.contractId}
                                workOrderId={workOrderId}
                                workOrderStatus={statusWorkOrder}
                                constructionTypeId={constructionTypeId}
                                constructionTypes={constructionTypes}
                                methodology={ProjectMethodologyEnum.Uer}
                            />
                        }
                    />
                    <Route
                        path="/viewOther/:otherId"
                        element={
                            <OtherViewModal
                                costRounding={costRounding}
                                constructionTypes={constructionTypes}
                                contractId={projectData?.contractId}
                                methodology={ProjectMethodologyEnum.Uer}
                                onClose={onEditOtherModalClose}
                            />
                        }
                    />
                    <Route
                        path="/editUer/:uerId/:workOrderPriceId"
                        element={
                            <CreateOrEditUer
                                chapter={filters.chapter}
                                onSave={onModalClose}
                                constructionTypeId={constructionTypeId}
                                contractId={projectData?.contractId}
                                workOrderStatus={statusWorkOrder}
                                noShowButton
                            />
                        }
                    />
                    <Route
                        path="editCoeff/:coeffId"
                        element={
                            <CreateOrEditRatio
                                onClose={onModalClose}
                                onConfirm={onModalClose}
                                workOrderStatus={statusWorkOrder}
                            />
                        }
                    />
                </Routes>
                {activeModal === ModalWorkOrderEnum.WorkOrderUseCoefficient && (
                    <WorkOrderUseCoefficient
                        workOrderObjects={patchedObjectForWorkOrderFilter}
                        constructionTypeId={constructionTypeId}
                        closeHandler={closeModal}
                        updaterApplyRatio={reloadWorkOrderTable}
                        objectId={filters?.objectId}
                    />
                )}

                <div ref={tableRef}>
                    {isShowTablePossible ? (
                        <>
                            {isOneOfThePriceChaptersSelected && (
                                <WorkOrderPrice
                                    ref={gridTableRef}
                                    workOrderId={workOrderId}
                                    chapter={filters.chapter}
                                    workOrderObjectId={filters.objectId}
                                    workOrderSubObjectId={filters.subObjectId}
                                    reload={state?.reload}
                                    workOrderStatus={statusWorkOrder}
                                    workOrderHasInProgress={workOrderHasInProgress}
                                    contractStatus={getContractData?.status}
                                    isAllActKC2Accepted={isAllActKC2Accepted}
                                    onRefresh={onActKCAddOrEdit}
                                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                                    costRounding={costRounding}
                                    contractId={getContractData?.id}
                                />
                            )}
                            {isOthersChapterSelected && (
                                <WorkOrderOtherTable
                                    ref={gridTableRef}
                                    section={filters.chapter}
                                    workOrderId={workOrderId}
                                    workOrderObjectId={filters.objectId}
                                    workOrderStatus={statusWorkOrder}
                                    workOrderHasInProgress={workOrderHasInProgress}
                                    reload={state?.reload}
                                    contractStatus={getContractData?.status}
                                    checkConstructionTypeHasActKC2={checkConstructionTypeHasActKC2Handler}
                                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                                    costRounding={costRounding}
                                />
                            )}

                            {isSummaryChapterSelected && <WorkOrderSummaryTable costRounding={costRounding} />}
                        </>
                    ) : (
                        <ChooseSectionText text="Для отображения данных выберите раздел, объект и подобъект" />
                    )}
                </div>
            </div>
        </ProjectPageLayout>
    );
};

WorkOrder.displayName = 'WorkOrder';
